import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import { storage } from '../utils/storage'
import { Platform } from '../utils/platform'
import { supabase } from '../services/supabase'
import type { Budget } from '@flows.cash/types'
import { RealtimeChannel } from '@supabase/supabase-js'

// Get the appropriate storage implementation
const getStorage = () => {
  if (Platform.isNative && (global as any).__FLOWS_STORAGE__) {
    return (global as any).__FLOWS_STORAGE__;
  }
  return storage;
};

interface BudgetState {
  budgets: Budget[]
  isLoading: boolean
  error: string | null
  lastFetched: number | null
  subscription: RealtimeChannel | null
  shouldFetch: boolean
  setShouldFetch: (value: boolean) => void
  fetchBudgets: (userId: string, forceRefresh?: boolean) => Promise<void>
  addBudget: (userId: string, budget: Omit<Budget, 'id' | 'user_id' | 'created_at' | 'updated_at'>) => Promise<void>
  updateBudget: (userId: string, id: string, budget: Partial<Budget>) => Promise<void>
  deleteBudget: (userId: string, id: string) => Promise<void>
  clearBudgets: () => void
  subscribeToChanges: (userId: string) => void
  unsubscribeFromChanges: () => void
}

// Cache duration in milliseconds (5 minutes)
const CACHE_DURATION = 5 * 60 * 1000;

export const useBudgetStore = create<BudgetState>()(
  persist(
    (set, get) => ({
      budgets: [],
      isLoading: false,
      error: null,
      lastFetched: null,
      subscription: null,
      shouldFetch: false,

      setShouldFetch: (value: boolean) => set({ shouldFetch: value }),

      fetchBudgets: async (userId: string, forceRefresh = false) => {
        const state = get();
        
        // Don't fetch if shouldFetch is false and not forcing refresh
        if (!state.shouldFetch && !forceRefresh) {
          console.log('Skipping budget fetch - shouldFetch is false');
          return;
        }

        const now = Date.now();

        // Return cached data if available and not expired
        if (!forceRefresh && 
            state.lastFetched && 
            state.budgets.length > 0 && 
            now - state.lastFetched < CACHE_DURATION) {
          console.log('Using cached budgets data');
          return;
        }

        set({ isLoading: true, error: null });
        try {
          console.log('Fetching budgets for user:', userId);

          const { data: budgets, error } = await supabase
            .from('budgets')
            .select('*')
            .eq('user_id', userId)
            .order('created_at', { ascending: false });

          if (error) throw error;

          console.log('Fetched budgets:', budgets);
          set({ 
            budgets, 
            isLoading: false,
            lastFetched: Date.now()
          });
        } catch (error) {
          console.error('Error fetching budgets:', error);
          set({
            error: error instanceof Error ? error.message : 'Failed to fetch budgets',
            isLoading: false,
          });
        }
      },

      addBudget: async (userId: string, budget): Promise<void> => {
        set({ isLoading: true, error: null });
        try {
          const now = new Date().toISOString();
          
          // Extract only the fields that exist in Supabase schema (snake_case)
          const supabaseBudget = {
            user_id: userId,
            name: budget.name,
            amount: budget.amount,
            currency: budget.currency || 'USD',
            period: budget.period,
            start_date: budget.start_date || budget.startDate,
            end_date: budget.end_date || budget.endDate,
            // Use category_id (singular) as BudgetManager does
            category_id: budget.category_id || budget.categoryId || null,
            spent: budget.spent || 0,
            remaining: budget.remaining || budget.amount,
            is_archived: budget.is_archived || false,
            created_at: now,
            updated_at: now,
            // Cast additional fields that might exist in database but not in interface
            ...((budget as any).source && { source: (budget as any).source }),
            ...((budget as any).assistant_session_id && { assistant_session_id: (budget as any).assistant_session_id }),
          };

          const { data, error } = await supabase
            .from('budgets')
            .insert(supabaseBudget)
            .select('*')
            .single();

          if (error) throw error;

          // Convert back to camelCase for local state
          const newBudget = {
            id: data.id,
            userId: data.user_id,
            name: data.name,
            amount: data.amount,
            currency: data.currency,
            originalAmount: data.amount,
            originalCurrency: data.currency,
            categoryId: data.category_id,
            period: data.period,
            startDate: data.start_date,
            endDate: data.end_date,
            createdAt: data.created_at,
            updatedAt: data.updated_at,
            spent: data.spent || 0,
            remaining: data.remaining || data.amount,
            isArchived: data.is_archived || false,
          };

          set((state) => ({
            budgets: [...state.budgets, newBudget],
            isLoading: false,
          }));
        } catch (error) {
          console.error('Error adding budget:', error);
          set({
            error: error instanceof Error ? error.message : 'Failed to add budget',
            isLoading: false,
          });
          throw error;
        }
      },

      updateBudget: async (_userId: string, id: string, budget) => {
        set({ isLoading: true, error: null });
        try {
          const updates = {
            ...budget,
            updated_at: new Date().toISOString(),
          };

          const { data, error } = await supabase
            .from('budgets')
            .update(updates)
            .eq('id', id)
            .select()
            .single();

          if (error) throw error;

          set((state) => ({
            budgets: state.budgets.map((b) => (b.id === id ? data : b)),
            isLoading: false,
            lastFetched: Date.now()
          }));
        } catch (error) {
          console.error('Error updating budget:', error);
          set({
            error: error instanceof Error ? error.message : 'Failed to update budget',
            isLoading: false,
          });
          throw error;
        }
      },

      deleteBudget: async (_userId: string, id: string) => {
        set({ isLoading: true, error: null });
        try {
          const { error } = await supabase
            .from('budgets')
            .delete()
            .eq('id', id);

          if (error) throw error;

          set((state) => ({
            budgets: state.budgets.filter((b) => b.id !== id),
            isLoading: false,
            lastFetched: Date.now()
          }));
        } catch (error) {
          console.error('Error deleting budget:', error);
          set({
            error: error instanceof Error ? error.message : 'Failed to delete budget',
            isLoading: false,
          });
          throw error;
        }
      },

      clearBudgets: () => {
        const state = get();
        if (state.subscription) {
          state.subscription.unsubscribe();
        }
        set({ 
          budgets: [], 
          error: null, 
          lastFetched: null,
          subscription: null 
        });
      },

      subscribeToChanges: (userId: string) => {
        const state = get();
        
        // Unsubscribe from any existing subscription
        if (state.subscription) {
          state.subscription.unsubscribe();
        }

        // Subscribe to budget changes
        const subscription = supabase
          .channel('budget_changes')
          .on(
            'postgres_changes',
            {
              event: '*',
              schema: 'public',
              table: 'budgets',
              filter: `user_id=eq.${userId}`
            },
            async (payload) => {
              console.log('Budget change received:', payload);
              
              // Refresh budgets data
              await get().fetchBudgets(userId, true);
            }
          )
          .subscribe();

        set({ subscription });
      },

      unsubscribeFromChanges: () => {
        const state = get();
        if (state.subscription) {
          state.subscription.unsubscribe();
          set({ subscription: null });
        }
      }
    }),
    {
      name: 'budgets-storage',
      storage: createJSONStorage(() => getStorage())
    }
  )
) 